import { AxiosInstance } from 'axios';
import { makeAutoObservable } from 'mobx';
import { AxiosResponseHelper } from 'utilities/AxiosResponseHelper';
import { InviteSendModel } from 'types/models/InviteSendModel';
import { INotificationsStore } from './NotificationsStore';
import { t } from 'i18next';
import { SingleInviteType, SingleUserType } from 'types/GlobalTypes';
import { IProfileStore } from './ProfileStore';

export interface IInviteStore {
  lastUpdated: number;
  invites: SingleInviteType[];
  isLoading: boolean;
  isCancelingInvite: boolean;
  setData(data: SingleInviteType[]): void;
  LoadData(apiInstance: AxiosInstance): Promise<void>;
  SendInvite(apiInstance: AxiosInstance, data: InviteSendModel, success: () => void, error: () => void): Promise<void>;
  ResendInvite(apiInstance: AxiosInstance, inviteId: string, success: () => void, error: () => void): Promise<void>;
  CancelInvite(
    apiInstance: AxiosInstance,
    inviteId: string | undefined,
    successCB: (response: any) => void,
    errorCB: (response: any) => void,
    notFoundCB: (response: any) => void
  ): void;
}

export class InviteStore implements IInviteStore {
  lastUpdated: number = 0;
  invites: SingleInviteType[] = [];
  userOrganizationData: SingleUserType;

  isLoading: boolean = false;
  isCancelingInvite: boolean = false;

  _notificationStore: INotificationsStore;
  _profileStore: IProfileStore;

  constructor(notificationStore: INotificationsStore, profileStore: IProfileStore) {
    this._notificationStore = notificationStore;
    this._profileStore = profileStore;

    makeAutoObservable(this);
  }

  setData(data: SingleInviteType[]) {
    this.invites = data;
    this.lastUpdated = Date.now();
  }

  public async LoadData(apiInstance: AxiosInstance) {
    this.isLoading = true;

    let response = await apiInstance.get(`api/invitations`);

    if (AxiosResponseHelper.IsSuccessfulResponse(response) && response.data?.data) {
      const r = response.data.data;
      this.setData(r);
    }

    this.isLoading = false;
  }

  public async SendInvite(apiInstance: AxiosInstance, data: InviteSendModel, success: () => void, error: () => void) {
    this.isLoading = true;
    let response = await apiInstance.post(`api/invitations`, data);
    if (AxiosResponseHelper.IsSuccessfulResponse(response)) {
      this._notificationStore.displayNotification({
        variant: 'success',
        title: t('common.success'),
        message: t('toastMessages.invitationSent'),
        response: response.data,
      });

      success();
      await this.LoadData(apiInstance);
    } else {
      this._notificationStore.displayNotification({
        response: response.data,
        variant: 'error',
        title: t('common.error'),
      });

      error();
    }
    this.isLoading = false;
  }

  public async ResendInvite(
    apiInstance: AxiosInstance, 
    invitationId: string, 
    success: () => void, 
    error: () => void
    ) {      
    this.isLoading = true;
    let response = await apiInstance.put(`api/invitations/resend/${invitationId}`);

    if (AxiosResponseHelper.IsSuccessfulResponse(response)) {
      this._notificationStore.displayNotification({
        response: response.data,
        variant: 'success',
        title: t('common.success'),
        message: t('toastMessages.invitationResent'),
      });

      success();
      await this.LoadData(apiInstance);
    } else {
      this._notificationStore.displayNotification({
        response: response.data,
        variant: 'error',
        title: t('common.error'),
        message: t('toastMessages.invitationError'),
      });

      error();
    }
    this.isLoading = false;
  }

  public async CancelInvite(
    apiInstance: AxiosInstance,
    inviteId: string,
    successCB: (response) => void,
    errorCB: (response) => void,
    notFoundCB: (response) => void
  ) {
    this.isCancelingInvite = true;
    let response = await apiInstance.delete(`api/invitations/${inviteId}`);

    if (AxiosResponseHelper.IsSuccessfulResponse(response)) {
      successCB(response);
      this._notificationStore.displayNotification({
        response: response.data,
        variant: 'success',
        title: t('common.success'),
        message: t('toastMessages.invitationCancelled'),
      });

      this.isCancelingInvite = false;
      return;
    }

    if (response.status === 404) {
      await this.LoadData(apiInstance);
      notFoundCB(response);
      this._notificationStore.displayNotification({
        response: response.data,
        variant: 'error',
        title: t('common.error'),
        message: t('toastMessages.invitationNotFound'),
      });

      this.isCancelingInvite = false;
      return;
    }

    this.isCancelingInvite = false;

    this._notificationStore.displayNotification({
      response: response.data,
      variant: 'error',
      title: t('common.error'),
      message: t('toastMessages.invitationError'),
    });
    errorCB(response);
  }
}